import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CreateExpenseTypeFormValues,
  UpdateExpenseTypeFormValues,
  createExpenseType,
  deleteExpenseType,
  getExpenseTypes,
  restoreExpenseType,
  updateExpenseType,
} from "../api/expenseTypes";
import { queryClient } from "@/api/queryClient";
import { ExpenseType } from "@/types/expenseType";
import { ApiResponse, PaginatedApiResponse, PaginationInfo } from "@/types/api";
import { AxiosRequestConfig } from "axios";

interface useExpenseTypeMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export const useGetExpenseTypes = (config?: AxiosRequestConfig) => {
  return useQuery<
    PaginatedApiResponse<ExpenseType[]>,
    Error,
    { data: ExpenseType[]; pagination: PaginationInfo }
  >({
    queryKey: ["expense-types", config?.params],
    queryFn: () => getExpenseTypes(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
  });
};

export const useGetExpenseTypesOptions = (config?: AxiosRequestConfig) => {
  return useQuery<ApiResponse<ExpenseType[]>, Error, ExpenseType[]>({
    queryKey: ["expense-types-options", config?.params],
    queryFn: () => getExpenseTypes(config),
    select: (response) => response.data,
  });
};

export const useCreateExpenseType = (
  options?: useExpenseTypeMutationOptions<ExpenseType>
) => {
  return useMutation({
    mutationFn: (data: CreateExpenseTypeFormValues) => createExpenseType(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-types"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useUpdateExpenseType = (
  options?: useExpenseTypeMutationOptions<ExpenseType>
) => {
  return useMutation({
    mutationFn: (data: UpdateExpenseTypeFormValues) =>
      updateExpenseType(data.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-types"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useDeleteExpenseType = (
  options?: useExpenseTypeMutationOptions<ExpenseType>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteExpenseType(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-types"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useRestoreExpenseType = (
  options?: useExpenseTypeMutationOptions<ExpenseType>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => restoreExpenseType(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-types"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};
